/**
 * Тексты и оформление статусной строки готовой работы.
 *
 * Правило формулировки (макет «Готовая работа», раздел «Точка меняет цвет по статусу»):
 * глагол в прошедшем времени плюс дата — кроме активных состояний, у которых даты ещё нет.
 * Цвета точки одной светлоты и насыщенности, различаются только тоном.
 */

export const READY_WORK_TONES = {
  ready: '#2f9e63',
  generating: '#2f7f9e',
  pending: '#9e7a2f',
  failed: '#9e3f2f',
  neutral: '#75757f',
};

const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * «18 августа в 21:44» (withTime) или «18 августа».
 *
 * @param {string|Date|null} value ISO-строка из generation.finished_at
 * @param {{ withTime?: boolean }} [options]
 * @returns {string} пустая строка, если даты нет или она нечитаема
 */
export function formatReadyAt(value, { withTime = true } = {}) {
  const date = toDate(value);
  if (!date) return '';

  const day = `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]}`;
  if (!withTime) return day;

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day} в ${hours}:${minutes}`;
}

/**
 * Русское склонение существительного при числе: 1 страница, 2 страницы, 5 страниц.
 */
export function pluralRu(count, one, few, many) {
  const n = Math.abs(Number(count) || 0) % 100;
  const n1 = n % 10;

  if (n > 10 && n < 20) return many;
  if (n1 > 1 && n1 < 5) return few;
  if (n1 === 1) return one;

  return many;
}

/**
 * Точка и текст статуса.
 *
 * @param {string} status статус документа
 * @param {{ finishedAt?: string|null, compact?: boolean }} [options] compact — мобильная строка (без времени)
 * @returns {{ tone: string, color: string, text: string }}
 */
export function resolveStatusPresentation(status, { finishedAt = null, compact = false } = {}) {
  const withTime = !compact;
  const at = formatReadyAt(finishedAt, { withTime });
  const suffix = at ? ` ${at}` : '';

  switch (status) {
    case 'full_generated':
    case 'approved':
      return { tone: 'ready', color: READY_WORK_TONES.ready, text: `Готова${suffix}` };
    case 'full_generating':
      return { tone: 'generating', color: READY_WORK_TONES.generating, text: 'Генерируется' };
    case 'pre_generating':
      return { tone: 'generating', color: READY_WORK_TONES.generating, text: 'Собираем структуру' };
    case 'pre_generated':
      return {
        tone: 'pending',
        color: READY_WORK_TONES.pending,
        text: compact ? 'Структура готова' : 'Структура готова, ждёт запуска',
      };
    case 'full_generation_failed':
      return { tone: 'failed', color: READY_WORK_TONES.failed, text: `Генерация прервалась${suffix}` };
    case 'pre_generation_failed':
      return { tone: 'failed', color: READY_WORK_TONES.failed, text: 'Не удалось собрать структуру' };
    case 'in_review':
      return { tone: 'pending', color: READY_WORK_TONES.pending, text: 'На проверке' };
    case 'rejected':
      return { tone: 'failed', color: READY_WORK_TONES.failed, text: 'Отклонена' };
    default:
      return { tone: 'neutral', color: READY_WORK_TONES.neutral, text: 'Черновик' };
  }
}

/**
 * «курсовая работа, 25 страниц» — тип работы и объём.
 *
 * @param {string} typeName название типа работы (documentType.name)
 * @param {number|null} pagesNum
 * @param {{ compact?: boolean }} [options] compact — мобильный вариант, без слова «работа»
 */
export function formatWorkMeta(typeName, pagesNum, { compact = false } = {}) {
  const parts = [];
  const type = String(typeName ?? '').trim().toLowerCase();

  if (type) {
    parts.push(compact ? type.replace(/\s*работа$/, '') : type);
  }

  const pages = Number(pagesNum);
  if (Number.isFinite(pages) && pages > 0) {
    parts.push(`${pages} ${pluralRu(pages, 'страница', 'страницы', 'страниц')}`);
  }

  return parts.join(', ');
}

/**
 * «5 разделов · 14 подпунктов» — размер содержания.
 * Титульный лист, оглавление и список литературы в счёт не идут: они не из contents.
 */
export function countContents(contents) {
  const list = Array.isArray(contents) ? contents : [];
  const topics = list.length;
  const subtopics = list.reduce(
    (sum, topic) => sum + (Array.isArray(topic?.subtopics) ? topic.subtopics.length : 0),
    0
  );

  if (!topics) return '';

  const parts = [`${topics} ${pluralRu(topics, 'раздел', 'раздела', 'разделов')}`];
  if (subtopics) {
    parts.push(`${subtopics} ${pluralRu(subtopics, 'подпункт', 'подпункта', 'подпунктов')}`);
  }

  return parts.join(' · ');
}

/**
 * Сводка титульного листа для строки «Титульный лист заполнен: …».
 *
 * В строку выносим то, по чему человек узнаёт свои данные: вуз, факультет, себя,
 * руководителя и год. Год в файл подставляется при экспорте (`WordDocumentService`,
 * `date('Y')`), поэтому здесь — текущий, а не поле формы.
 *
 * @param {object|null} titlePage structure.title_page
 * @param {{ compact?: boolean, now?: Date }} [options] compact — мобильная строка (вуз, студент, год)
 * @returns {{ isEmpty: boolean, summary: string }}
 */
export function summarizeTitlePage(titlePage, { compact = false, now = new Date() } = {}) {
  const page = titlePage && typeof titlePage === 'object' ? titlePage : {};
  const values = Object.values(page).map((value) => String(value ?? '').trim());
  const isEmpty = values.every((value) => value === '');

  if (isEmpty) {
    return { isEmpty: true, summary: '' };
  }

  const order = compact
    ? ['university', 'student_name']
    : ['university', 'faculty', 'student_name', 'supervisor_name'];

  const parts = order.map((key) => String(page[key] ?? '').trim()).filter(Boolean);
  parts.push(String(now.getFullYear()));

  return { isEmpty: false, summary: parts.join(' · ') };
}
